import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  Pipe,
  PipeTransform,
  signal,
  untracked,
} from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import {
  applyWhen,
  disabled,
  FieldContext,
  form,
  FormField,
  required,
  validate,
  ValidationResult,
} from "@angular/forms/signals";
import { AudienceExpressionType, AudienceMatchType, TacticType } from "@front/m19-api-client";
import { SdTargetingType, SegmentConfigType, SegmentEx, SupportedAudienceLookback } from "@front/m19-models";
import { SdStrategyEx } from "@front/m19-models/strategy/SdStrategyEx";
import { AccountSelectionService, SegmentService } from "@front/m19-services";
import {
  IButtonDirective,
  IIconComponent,
  ILabelComponent,
  ISelectMenuImports,
  ITooltipDirective,
  ModalService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  ProductSegmentModalComponent,
  ProductSegmentModalData,
} from "@m19-board/segments/product-segment-modal.component";
import { combineLatest, map, switchMap } from "rxjs";
import {
  AudienceMatchTypeOption,
  EventTypeLabel,
  MatchTypeLabel,
  SdTargetingFormModel,
  SdTargetingFormModelWithoutIsValid,
  TargetingTypeLabel,
} from "./sd-targeting-form.types";

@Pipe({
  name: "sdTargetingTypeFormatted",
  standalone: true,
})
export class SdTargetingTypeFormattedPipe implements PipeTransform {
  transform(value: SdTargetingType): string {
    return TargetingTypeLabel[value];
  }
}

@Pipe({
  name: "sdEventTypeFormatted",
  standalone: true,
})
export class SdEventTypeFormattedPipe implements PipeTransform {
  transform(value: AudienceExpressionType): string {
    return EventTypeLabel[value];
  }
}

@Pipe({
  name: "sdMatchTypeFormatted",
  standalone: true,
})
export class SdMatchTypeFormattedPipe implements PipeTransform {
  transform(value: AudienceMatchType): string {
    return MatchTypeLabel[value];
  }
}

@Component({
  selector: "app-sd-targeting-form",
  imports: [
    ISelectMenuImports,
    TranslocoDirective,
    IButtonDirective,
    SdTargetingTypeFormattedPipe,
    SdEventTypeFormattedPipe,
    SdMatchTypeFormattedPipe,
    IIconComponent,
    ITooltipDirective,
    ILabelComponent,
    FormField,
  ],
  templateUrl: "./sd-targeting-form.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdTargetingFormComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly segmentService = inject(SegmentService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);

  public readonly TargetingType = SdTargetingType;
  public readonly AudienceExpressionType = AudienceExpressionType;
  public readonly MatchTypeLabel = MatchTypeLabel;
  readonly supportedLookback = SupportedAudienceLookback;
  readonly eventTypes = [AudienceExpressionType.views, AudienceExpressionType.purchases];

  public readonly existingStrategy = input<SdStrategyEx | undefined>(undefined);
  public readonly existingTargeting = input<SdTargetingFormModelWithoutIsValid | undefined>(undefined);
  public readonly formChange = output<SdTargetingFormModel>();

  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$);
  private readonly allowedSegments = toSignal(
    combineLatest([
      toObservable(this.existingStrategy),
      this.accountSelectionService.singleAccountMarketplaceSelection$,
    ]).pipe(
      switchMap(([strategy, accountMarketplace]) => {
        if (!strategy)
          return this.segmentService
            .getSegments(accountMarketplace.accountId, accountMarketplace.marketplace)
            .pipe(
              map((segments) =>
                Array.from(segments.values()).filter((s) => s.segmentType === SegmentConfigType.ProductSegment),
              ),
            );
        return this.segmentService.getAllowedSegmentsForStrategy(strategy);
      }),
    ),
  );

  public readonly segmentQueryFilter = signal("");

  readonly matchTypes = computed<AudienceMatchTypeOption[]>(() => {
    const disableSimilarProduct =
      this.targetingForm.audienceExpressionType().value() === AudienceExpressionType.purchases;
    const disableRelatedProduct = this.targetingForm.audienceExpressionType().value() === AudienceExpressionType.views;
    return [
      {
        AudienceMatchType: AudienceMatchType.exactProduct,
        label: MatchTypeLabel[AudienceMatchType.exactProduct],
      },
      {
        AudienceMatchType: AudienceMatchType.similarProduct,
        label: MatchTypeLabel[AudienceMatchType.similarProduct],
        disabled: disableSimilarProduct,
        tooltip: disableSimilarProduct
          ? this.translocoService.translate("sd-targeting-add-popup.create_error_similar")
          : undefined,
      },
      {
        AudienceMatchType: AudienceMatchType.relatedProduct,
        label: MatchTypeLabel[AudienceMatchType.relatedProduct],
        disabled: disableRelatedProduct,
        tooltip: disableRelatedProduct
          ? this.translocoService.translate("sd-targeting-add-popup.create_error_related")
          : undefined,
      },
    ];
  });

  readonly targetingTypes = computed(() => {
    const types = [
      {
        label: TargetingTypeLabel[SdTargetingType.PRODUCT_AI_TARGETING],
        value: SdTargetingType.PRODUCT_AI_TARGETING,
        disabled: !this.isAutoAlgoExplorationAvailable(this.existingStrategy()),
      },
      {
        label: TargetingTypeLabel[SdTargetingType.PRODUCT],
        value: SdTargetingType.PRODUCT,
      },
      {
        label: TargetingTypeLabel[SdTargetingType.REMARKETING],
        value: SdTargetingType.REMARKETING,
        testid: "remarketing-targeting-opt",
      },
    ];
    return types;
  });

  private readonly formModel = linkedSignal<SdTargetingFormModelWithoutIsValid>(() => {
    if (this.existingTargeting()) {
      return {
        targetingType: this.existingTargeting()!.targetingType,
        segment: this.existingTargeting()!.segment,
        audienceExpressionType: this.existingTargeting()!.audienceExpressionType,
        audienceMatchType: this.existingTargeting()!.audienceMatchType,
        audienceLookback: this.existingTargeting()!.audienceLookback,
      };
    }
    return {
      targetingType: this.isAutoAlgoExplorationAvailable(this.existingStrategy())
        ? SdTargetingType.PRODUCT_AI_TARGETING
        : null,
      segment: null,
      audienceExpressionType: AudienceExpressionType.views,
      audienceMatchType: AudienceMatchType.exactProduct,
      audienceLookback: 30,
    };
  });

  readonly targetingForm = form(this.formModel, (schema) => {
    required(schema.targetingType);
    required(schema.audienceExpressionType);
    required(schema.audienceMatchType);
    required(schema.audienceLookback);

    disabled(schema.segment, ({ valueOf }) => valueOf(schema.targetingType) !== SdTargetingType.PRODUCT);

    applyWhen(
      schema,
      ({ valueOf }) => valueOf(schema.targetingType) === SdTargetingType.PRODUCT,
      (productSchema) => {
        required(productSchema.segment);
      },
    );

    applyWhen(
      schema,
      ({ valueOf }) => valueOf(schema.targetingType) === SdTargetingType.REMARKETING,
      (remarketingSchema) => {
        validate(remarketingSchema, (ctx) => this.validateRemarketingTargeting(ctx));
      },
    );
  });

  readonly filteredSegments = computed(() => {
    return this.allowedSegments()?.filter((s) =>
      s.name.toLowerCase().includes(this.segmentQueryFilter().toLowerCase()),
    );
  });

  readonly submitBtnLabel = computed(() => {
    return this.targetingForm.targetingType().value() === SdTargetingType.PRODUCT_AI_TARGETING
      ? this.translocoService.translate("sd-targeting-add-popup.enable_ai-powered_targeting")
      : this.translocoService.translate("common.create");
  });

  constructor() {
    effect(() => {
      this.formChange.emit({
        ...this.targetingForm().value(),
        isValid: this.targetingForm().valid(),
      });
    });

    effect(() => {
      const targetingType = this.targetingForm().value().targetingType;
      const audienceExpressionType = this.targetingForm().value().audienceExpressionType;
      const audienceMatchType = this.targetingForm().value().audienceMatchType;

      if (targetingType !== SdTargetingType.REMARKETING) return;
      untracked(() => {
        if (
          audienceExpressionType === AudienceExpressionType.views &&
          audienceMatchType === AudienceMatchType.relatedProduct
        ) {
          this.targetingForm.audienceMatchType().value.set(AudienceMatchType.similarProduct);
        }
        if (
          audienceExpressionType === AudienceExpressionType.purchases &&
          audienceMatchType === AudienceMatchType.similarProduct
        ) {
          this.targetingForm.audienceMatchType().value.set(AudienceMatchType.relatedProduct);
        }
      });
    });
  }

  createNewSegment() {
    if (this.accountMarketplace()?.accountId && this.accountMarketplace()?.marketplace) {
      const ref = this.modalService.openModal<ProductSegmentModalData, SegmentEx>(ProductSegmentModalComponent, {
        modalTitle: `${this.translocoService.translate("common.create")} ${this.translocoService.translate("product-segment-modal.product_segment")}`,
        data: {
          accountId: this.accountMarketplace()!.accountId,
          marketplace: this.accountMarketplace()!.marketplace,
        },
      });

      ref.afterClosed().subscribe((segment: SegmentEx) => {
        if (segment) {
          this.targetingForm.segment().value.set(segment);
        }
      });
    }
  }

  private validateRemarketingTargeting(context: FieldContext<SdTargetingFormModelWithoutIsValid>): ValidationResult {
    const value = context.value();
    const strategy = this.existingStrategy();
    if (!strategy) return null;

    return strategy.audienceTargetings.every(
      (aud) =>
        !(
          aud.expressionType === value.audienceExpressionType &&
          aud.matchType === value.audienceMatchType &&
          aud.lookback === value.audienceLookback
        ),
    )
      ? null
      : { kind: "audienceAlreadyExists" };
  }

  private isAutoAlgoExplorationAvailable(strategy: SdStrategyEx | undefined): boolean {
    return !strategy || !strategy?.autoAlgoExplorationEnabled;
  }
}
